import { useEffect, useRef, useState } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { GOOGLE_MAPS_API_KEY } from "@/lib/googleMaps";

interface Props {
  latitude: number;
  longitude: number;
  title: string;
  imageUrl?: string | null;
  propertyId: string;
  streetAddress?: string | null;
  onAddressResolved?: (address: string) => void;
}

let optionsSet = false;
if (!optionsSet) {
  setOptions({ key: GOOGLE_MAPS_API_KEY, v: "weekly" });
  optionsSet = true;
}

const PropertyDetailMap = ({
  latitude,
  longitude,
  title,
  imageUrl,
  propertyId,
  streetAddress,
  onAddressResolved,
}: Props) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(streetAddress || null);

  useEffect(() => {
    let map: any;
    let marker: any;
    let infoWindow: any;

    (importLibrary as any)("maps").then(() => {
      if (!mapRef.current) return;

      map = new window.google.maps.Map(mapRef.current, {
        center: { lat: latitude, lng: longitude },
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
      });

      marker = new window.google.maps.Marker({
        map,
        position: { lat: latitude, lng: longitude },
        title,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: "hsl(160,84%,39%)",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 3,
        },
      });

      infoWindow = new window.google.maps.InfoWindow();

      const imgHtml = imageUrl
        ? `<img src="${imageUrl}" alt="${title}" style="width:160px;height:100px;object-fit:cover;border-radius:8px;margin-bottom:6px;" />`
        : "";

      marker.addListener("click", () => {
        infoWindow.setContent(`
          <div style="max-width:180px;font-family:system-ui,sans-serif;">
            ${imgHtml}
            <p style="font-weight:600;font-size:12px;margin:0 0 6px;color:#1a1a1a;">${title}</p>
            <a href="/listings/${propertyId}" style="font-size:11px;color:hsl(160,84%,39%);text-decoration:none;font-weight:500;">
              View Details →
            </a>
          </div>
        `);
        infoWindow.open(map, marker);
      });

      // Reverse geocode if no street address
      if (!streetAddress) {
        const geocoder = new (window.google.maps as any).Geocoder();
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results: any[], status: string) => {
            if (status === "OK" && results?.[0]) {
              const addr = results[0].formatted_address;
              setResolvedAddress(addr);
              onAddressResolved?.(addr);
            }
          }
        );
      }
    });
  }, [latitude, longitude]);

  return (
    <div>
      <div
        ref={mapRef}
        className="w-full rounded-xl border overflow-hidden"
        style={{ height: 300 }}
      />
      {resolvedAddress && (
        <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
          <span className="shrink-0 mt-0.5">📍</span>
          <span>{resolvedAddress}</span>
        </p>
      )}
    </div>
  );
};

export default PropertyDetailMap;
